import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

function TermsOfServicePage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - OmniButler</title>
      </Helmet>
      <main className="mt-14">
        {/* Back to Home Link */}
        <div className="container py-6">
          <Link
            to="/"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium transition-colors duration-300 group"
          >
            <FaArrowLeft className="mr-2 text-sm transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Home
          </Link>
        </div>

        <section className="container py-16">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-16">
              <strong>Last Updated: July 24, 2025</strong>
            </p>

            {/* Terms Content */}
            <div className="space-y-12">
              {/* Section 1 */}
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  1. Agreement to Terms
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  By accessing or using OmniButler (&quot;Service&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you disagree with any part of these terms, you do not have permission to access the Service.
                </p>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="text-3xl font-bold mb-4">2. Description of Service</h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  OmniButler is an AI-powered personal financial management platform that helps you:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                  <li><strong>View all your finances in one place</strong> through secure bank connections via Plaid</li>
                  <li><strong>Get intelligent insights</strong> powered by advanced AI (OpenAI, Anthropic, Together AI)</li>
                  <li><strong>Find financial documents instantly</strong> with Gmail integration</li>
                  <li><strong>Chat naturally about your finances</strong> via WhatsApp</li>
                  <li><strong>Share selected data with family members</strong> through secure family accounts</li>
                </ul>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4">
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>IMPORTANT</strong>: OmniButler is a financial information service only. We help you organize and understand your finances, but we do NOT provide financial, investment, tax, legal, or accounting advice.
                  </p>
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  3. Account Registration and Security
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">3.1 Authentication</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li><strong>We NEVER see or store passwords</strong> - authentication is handled entirely by Google</li>
                      <li>Accounts are created exclusively through Google OAuth</li>
                      <li>You must have a valid Google account to use OmniButler</li>
                      <li>You are responsible for maintaining the security of your Google account</li>
                      <li>Enable two-factor authentication on your Google account for added security</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">3.2 Accurate Information</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>You must provide accurate information during registration</li>
                      <li>You must be at least 18 years old to use our Service</li>
                      <li>One account per person (no shared accounts except family features)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">3.3 Account Security</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Immediately notify us of any unauthorized access</li>
                      <li>You are responsible for all activities under your account</li>
                      <li>We are not liable for losses due to compromised Google accounts</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  4. Acceptable Use Policy
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">4.1 You May:</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Use the Service for personal financial management</li>
                      <li>Connect legitimate bank accounts you own or have authorization to access</li>
                      <li>Share data within family units with member consent</li>
                      <li>Export your own data</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">4.2 You May NOT:</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Access accounts you don&apos;t own without authorization</li>
                      <li>Use the Service for illegal activities</li>
                      <li>Attempt to reverse engineer or hack the Service</li>
                      <li>Resell or commercialize the Service</li>
                      <li>Violate any third-party rights</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  5. Third-Party Services
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">5.1 Plaid Integration</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-3">
                      <li><strong>We NEVER see or store your bank login credentials</strong></li>
                      <li>When you connect a bank account:
                        <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                          <li>You log in directly on your bank&apos;s website through Plaid&apos;s secure interface</li>
                          <li>Plaid handles all authentication with bank-level security</li>
                          <li>We only receive read-only access to your financial data</li>
                        </ul>
                      </li>
                      <li>By connecting accounts, you agree to <a href="https://plaid.com/legal/" className="text-indigo-600 hover:text-indigo-700" target="_blank" rel="noopener noreferrer">Plaid&apos;s Terms</a></li>
                      <li>You can disconnect bank accounts instantly at any time</li>
                      <li>We are not responsible for Plaid service interruptions</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">5.2 Google Services</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Gmail access requires agreeing to Google&apos;s terms</li>
                      <li>Calendar integration follows Google API policies</li>
                      <li>We comply with Google API Services User Data Policy</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">5.3 WhatsApp/Twilio</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>WhatsApp integration is optional</li>
                      <li>Message rates may apply from your carrier</li>
                      <li>You must own the phone number you register</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  6. Financial Data and Advice Disclaimer
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">6.1 Not Financial Advice</h3>
                    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-4">
                      <p className="text-gray-700 dark:text-gray-300 font-bold">
                        THIS SERVICE DOES NOT PROVIDE FINANCIAL, INVESTMENT, TAX, LEGAL, OR ACCOUNTING ADVICE.
                      </p>
                    </div>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>OmniButler provides information and insights for your personal use only</li>
                      <li>We are NOT a financial planner, broker, or tax advisor</li>
                      <li>We are NOT a fiduciary and have no fiduciary duties to you</li>
                      <li>ALWAYS consult qualified professionals before making financial decisions</li>
                      <li>Any projections or estimates are for informational purposes only</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">6.2 Data Accuracy</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>We display data as received from connected services</li>
                      <li>We are not responsible for bank data accuracy</li>
                      <li>Balance estimates between syncs are approximations</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">6.3 AI Limitations and Accuracy</h3>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-4">
                      <p className="text-gray-700 dark:text-gray-300">
                        <strong>IMPORTANT</strong>: Our AI features have limitations:
                      </p>
                    </div>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>AI responses may occasionally be inaccurate or inappropriate for your situation</li>
                      <li>AI cannot replace human judgment for important financial decisions</li>
                      <li>Always verify AI-generated information independently</li>
                      <li>We don&apos;t guarantee the accuracy, completeness, or timeliness of AI responses</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">6.4 Service Availability</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Service provided &quot;AS IS&quot; and &quot;AS AVAILABLE&quot;</li>
                      <li>We don&apos;t guarantee uninterrupted or error-free operation</li>
                      <li>Bank connections may fail and require re-authentication</li>
                      <li>Scheduled maintenance may cause temporary unavailability</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  7. Privacy and Data Rights
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">7.1 Data Collection</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>We collect only necessary data as outlined in our Privacy Policy</li>
                      <li>You maintain ownership of your financial data</li>
                      <li>We process data according to your instructions</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">7.2 Family Sharing</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-3">
                      <li><strong>Family units support up to 6 members</strong> (including the admin)</li>
                      <li>Family admins can invite members via email</li>
                      <li>Each member:
                        <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                          <li>Has their own login credentials</li>
                          <li>Controls which accounts to share</li>
                          <li>Can view only explicitly shared data</li>
                          <li>Can leave the family unit at any time</li>
                        </ul>
                      </li>
                      <li>Historical shared data remains visible to family after a member leaves</li>
                      <li>Family admins can remove members but cannot access their unshared data</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  8. Fees and Payments
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">8.1 Subscription Plans</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li><strong>Free Tier</strong>: Basic features with limited bank connections</li>
                      <li><strong>Premium</strong>: Full features, unlimited connections, priority support</li>
                      <li>Current pricing displayed at signup and in account settings</li>
                      <li>Prices may change with 30 days notice to existing customers</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">8.2 Billing</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Subscriptions renew automatically</li>
                      <li>Cancel anytime - access continues until period ends</li>
                      <li>No refunds for partial periods</li>
                      <li>Prorated upgrades, full-period downgrades</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">8.3 Free Trial</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>New users may receive a free trial period</li>
                      <li>Credit card required unless specified otherwise</li>
                      <li>Automatically converts to paid unless cancelled</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  9. Intellectual Property
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">9.1 Our IP</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>OmniButler name, logo, and software are our property</li>
                      <li>You receive a limited license to use the Service</li>
                      <li>No rights to copy or distribute our software</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">9.2 Your Content</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>You retain ownership of your financial data</li>
                      <li>You grant us license to process data for Service operation</li>
                      <li>We may anonymize and aggregate data for improvements</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  10. Limitation of Liability
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">10.1 No Warranties</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Service provided &quot;AS IS&quot; without warranties</li>
                      <li>We don&apos;t guarantee accuracy or availability</li>
                      <li>Use at your own risk</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">10.2 Liability Limits</h3>
                    <div className="bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-400 p-4 mb-4">
                      <p className="text-gray-700 dark:text-gray-300 font-bold mb-2">
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                      </p>
                    </div>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                      <li>We are NOT liable for any indirect, incidental, special, consequential, or punitive damages</li>
                      <li>We are NOT liable for:
                        <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                          <li>Lost profits or revenues</li>
                          <li>Financial losses from decisions based on our Service</li>
                          <li>Data loss beyond our reasonable control</li>
                          <li>Third-party service failures (Plaid, banks, etc.)</li>
                          <li>Unauthorized access despite reasonable security</li>
                        </ul>
                      </li>
                    </ul>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4">
                      <p className="text-gray-700 dark:text-gray-300">
                        <strong>Our total liability is limited to the amount you paid us in the 12 months before the claim arose.</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 11 */}
              <div>
                <h2 className="text-3xl font-bold mb-4">11. Indemnification</h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  You agree to indemnify OmniButler against claims arising from:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Your violation of these Terms</li>
                  <li>Your use of the Service</li>
                  <li>Disputes with family members over shared data</li>
                  <li>Unauthorized access to third-party accounts</li>
                </ul>
              </div>

              {/* Section 12 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  12. Termination
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">12.1 Your Rights</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Cancel subscription anytime</li>
                      <li>Request account deletion</li>
                      <li>Export your data before termination</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">12.2 Our Rights</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Terminate for Terms violations</li>
                      <li>Suspend for suspicious activity</li>
                      <li>Discontinue Service with 60 days notice</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">12.3 Effect of Termination</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Access to the Service ends immediately</li>
                      <li>Your data will be deleted within 30 days per our Privacy Policy</li>
                      <li>You may export your data before termination</li>
                      <li>No refund for current billing period</li>
                      <li>Some anonymized data may be retained for legal compliance</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 13 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  13. Dispute Resolution
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">13.1 Informal Resolution</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Contact support first</li>
                      <li>Good faith effort to resolve</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">13.2 Binding Arbitration</h3>
                    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-4">
                      <p className="text-gray-700 dark:text-gray-300 font-bold">
                        PLEASE READ CAREFULLY - THIS AFFECTS YOUR LEGAL RIGHTS:
                      </p>
                    </div>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Disputes resolved through binding arbitration (not court)</li>
                      <li>Arbitration conducted by JAMS under JAMS Streamlined Rules</li>
                      <li>Arbitration location: Ontario or mutually agreed location</li>
                      <li><strong>NO CLASS ACTIONS</strong>: Claims must be brought individually</li>
                      <li>Exception: Either party may use small claims court for qualifying disputes</li>
                      <li>You have 30 days from first use to opt out of arbitration by emailing legal@omnibutler.com</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 14 */}
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  14. Changes to Terms
                </h2>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>We may update Terms with 30 days notice</li>
                  <li>Continued use constitutes acceptance</li>
                  <li>Material changes notified via email</li>
                </ul>
              </div>

              {/* Section 15 */}
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  15. Miscellaneous
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">15.1 Entire Agreement</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>These Terms plus Privacy Policy constitute entire agreement</li>
                      <li>Supersedes prior agreements</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">15.2 Severability</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Invalid provisions don&apos;t affect remainder</li>
                      <li>Terms interpreted to be enforceable</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">15.3 Governing Law</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Governed by laws of Ontario, Canada</li>
                      <li>Excluding conflict of law provisions</li>
                      <li>Any legal action must be filed within one year</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">15.4 Assignment</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>You cannot assign these Terms</li>
                      <li>We may assign in connection with acquisition</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 16 */}
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  16. Contact Information
                </h2>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <p><strong>General Support</strong>: <a href="mailto:support@omnibutler.com" className="text-indigo-600 hover:text-indigo-700">support@omnibutler.com</a></p>
                  <p><strong>Privacy Concerns</strong>: <a href="mailto:privacy@omnibutler.com" className="text-indigo-600 hover:text-indigo-700">privacy@omnibutler.com</a></p>
                  <p><strong>Legal Inquiries</strong>: <a href="mailto:legal@omnibutler.com" className="text-indigo-600 hover:text-indigo-700">legal@omnibutler.com</a></p>
                </div>
              </div>

              {/* Service-Specific Terms */}
              <div className="border-t pt-8">
                <h2 className="text-3xl font-bold mb-6">
                  Service-Specific Terms
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">A. AI Features</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>AI responses are generated by third-party models (OpenAI, Anthropic, Together AI)</li>
                      <li>Responses are probabilistic and may contain errors or hallucinations</li>
                      <li>AI has no understanding of your specific financial situation</li>
                      <li>NEVER make financial decisions based solely on AI responses</li>
                      <li>We log interactions to improve service quality</li>
                      <li>You may opt out of interaction logging in settings</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">B. Data Syncing</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Bank data typically syncs every 4-6 hours (not real-time)</li>
                      <li>Premium subscribers get more frequent sync options</li>
                      <li>Manual sync available (rate limits apply)</li>
                      <li>Sync may fail if bank requires re-authentication</li>
                      <li>Transaction categorization happens after sync completes</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">C. Beta Features</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      Features marked &quot;Beta&quot; are experimental
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      Beta features:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                      <li>May contain bugs or errors</li>
                      <li>May be discontinued or changed without notice</li>
                      <li>Are provided &quot;AS IS&quot; with no warranties</li>
                      <li>Should not be relied upon for critical tasks</li>
                    </ul>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li>Your feedback helps improve beta features</li>
                      <li>No compensation provided for beta testing</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Footer note */}
              <div className="border-t pt-8 mt-12">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-6">
                  <p className="text-lg text-gray-700 dark:text-gray-300 font-semibold text-center">
                    By using OmniButler, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default TermsOfServicePage;